import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap, timeout } from 'rxjs/operators';
import {
    CACHED_HEADER,
    CachedPayload,
    CachedPOSTRequest,
    CachedRequest,
    ONLINE_HEADER,
} from '../classes/cached-request';
import { CacheableURLsInjectionToken, URL_MATCHES } from '../interfaces/cacheable-urls.interface';
import { cacheHttpCall } from '../store/actions/cache.action';
import { addRequestToQueue } from '../store/actions/request-queue.actions';
import { getCachedResponseByUrl } from '../store/selectors/cache.selector';
import { PaceOfflineFeatureState } from '../store/state';
import { OfflineService } from './offline.service';
import { OfflinePluginDoubleCacheError } from '../classes/pace-offline.errors';

const HTTP_ERROR = 0;

@Injectable()
export class OfflineInterceptorService implements HttpInterceptor {
    private readonly CACHEABLE_REQUEST_METHODS = ['post', 'put', 'delete'];
    public constructor(
        @Inject(CacheableURLsInjectionToken) private readonly cacheableURLs: URL_MATCHES,
        private store: Store<PaceOfflineFeatureState>,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private offlineService: OfflineService,
    ) {}

    public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (req.method === 'GET' || req.headers.has(ONLINE_HEADER)) {
            return next.handle(req);
        }
        const cacheableURL = this.cacheableURLs.find((url) =>
            typeof url === 'string' ? req.url.indexOf(url) > -1 : url.test(req.url),
        );

        // if non cacheableURL then send the request to backend
        if (typeof cacheableURL === 'undefined') {
            return next.handle(req);
        }
        // check device is online
        if (navigator.onLine) {
            if (req.headers.has(CACHED_HEADER)) {
                // if online then remove the cache header before sending it to backend
                const clonedReq = req.clone({ headers: req.headers.delete(CACHED_HEADER) });
                return next.handle(clonedReq).pipe(
                    tap((response) => {
                        if (response instanceof HttpResponse) {
                            // TODO: add logic for regex support
                            if (typeof cacheableURL === 'string' && clonedReq.method === 'GET') {
                                this.offlineService.setRequest(`GET: ${cacheableURL}`);
                                this.log.info(
                                    `OfflineInterceptorService: intercept: request url: ${clonedReq.url}, data count: ${response.body?.d?.results?.length}`,
                                );
                                // add the response to cache so as to provide offline flexibility
                                this.store.dispatch(
                                    cacheHttpCall({
                                        url: cacheableURL,
                                        response: response.body,
                                    }),
                                );
                            } else {
                                const endpoint = this.getEndpoint(clonedReq);
                                if (endpoint) {
                                    this.offlineService.setRequest(`POST: ${endpoint}`);
                                }
                                this.log.info(
                                    `OfflineInterceptorService: intercept: url: ${clonedReq.url}, endpoint: ${
                                        endpoint ?? ''
                                    }, response status: ${response.status}`,
                                );
                            }
                        }
                    }),
                    catchError((error) => {
                        if (req.method === 'GET') {
                            this.offlineService.setRequest(`GET: ${cacheableURL}`);
                            this.log.error(
                                `OfflineInterceptorService: intercept: GET: ${
                                    clonedReq.url
                                } is served with cached data due to error: ${JSON.stringify(
                                    error,
                                    Object.getOwnPropertyNames(error),
                                )} `,
                            );
                            return this.store.select(getCachedResponseByUrl, { url: cacheableURL }).pipe(
                                map((state) => {
                                    if (typeof state !== 'undefined') {
                                        return new HttpResponse({ body: state, status: HTTP_ERROR });
                                    }
                                    throw new HttpErrorResponse({
                                        status: HTTP_ERROR,
                                        statusText: 'no cache data available',
                                        url: req.url,
                                    });
                                }),
                                take(1),
                            );
                        }
                        const endpoint = this.getEndpoint(clonedReq);
                        if (endpoint) {
                            this.offlineService.setRequest(`POST: ${endpoint}`);
                        }
                        this.log.error(
                            `OfflineInterceptorService: intercept: ${clonedReq.url} with endpoint ${
                                endpoint ?? ''
                            }: ${JSON.stringify(error, Object.getOwnPropertyNames(error))} `,
                        );

                        return throwError(() => error);
                    }),
                );
            } else {
                // Even if online, cache the request to not lose data if connection gets lost during sending.
                if (this.CACHEABLE_REQUEST_METHODS.includes(req.method.toLocaleLowerCase())) {
                    const cachedRequest = new CachedRequest(req);
                    return this.savePostRequest(cachedRequest);
                }
                return next.handle(req);
            }
        } else {
            // THIS IS OFFLINE
            if (
                req.headers.has(CACHED_HEADER) &&
                this.CACHEABLE_REQUEST_METHODS.includes(req.method.toLocaleLowerCase())
            ) {
                return throwError(
                    () =>
                        new OfflinePluginDoubleCacheError(
                            `OfflineInterceptorService: intercept: trying to add an already cached post request to the queue again`,
                        ),
                );
            }

            // if request is not stored in the cache and request is 'POST', 'PUT', 'DELETE'
            if (this.CACHEABLE_REQUEST_METHODS.includes(req.method.toLocaleLowerCase())) {
                const cachedRequest = new CachedRequest(req);
                return this.savePostRequest(cachedRequest);
            }

            // if device is offline and request is not 'POST', 'PUT', 'DELETE', throw error
            this.log.error(`OfflineInterceptorService: intercept: Invalid HTTP method while being offline.`);
            return throwError(() => new Error(`Invalid HTTP method while being offline. Request: ${req}`));
        }
    }

    private savePostRequest(cachedRequest: CachedRequest): Observable<HttpResponse<unknown>> {
        const timeoutForSavingToStore = 3000;
        cachedRequest.addAdditionalHeader(CACHED_HEADER);
        const cachedPOSTRequest: CachedPOSTRequest = cachedRequest.createCachedPOSTRequest();
        return this.offlineService.pendingRequestCount$.pipe(
            take(1),
            tap((count) => {
                this.log.info(`OfflineInterceptorService: savePostRequest: initial pending requests count: ${count}`);
                this.store.dispatch(
                    addRequestToQueue({
                        request: cachedPOSTRequest,
                    }),
                );
            }),
            mergeMap(() =>
                combineLatest([this.offlineService.selectRequestQueue$, this.offlineService.stateHydration$]),
            ),
            // For unknown cases where state doesn't get saved to offline storage
            timeout(timeoutForSavingToStore),
            filter(([, isSavedToStorage]) => isSavedToStorage),
            take(1),
            mergeMap(([requests, isSavedToStorage]) => {
                this.log.info(
                    `OfflineInterceptorService: savePostRequest: updated pending requests count: ${requests.length}`,
                );
                const isRequestAdded = requests.some((req) => req.id === cachedPOSTRequest.id);
                if (isRequestAdded && isSavedToStorage) {
                    this.log.info(
                        `OfflineInterceptorService: savePostRequest: successfully added ${cachedRequest.getCachedPayloadEndpoint()} request with id ${
                            cachedPOSTRequest.id
                        } to queue`,
                    );
                    return of(
                        new HttpResponse({
                            body: {
                                d: {},
                            },
                            status: 200,
                        }),
                    );
                } else {
                    this.log.warn(
                        `OfflineInterceptorService: savePostRequest: could not add ${cachedRequest.getCachedPayloadEndpoint()} request with id ${
                            cachedPOSTRequest.id
                        } to queue`,
                    );
                    return throwError(() => 'OFFLINE_STORE_SAVE_ERROR');
                }
            }),
            catchError(() => {
                this.log.warn(
                    `OfflineInterceptorService: savePostRequest: could not add request ${cachedRequest.getCachedPayloadEndpoint()} with id ${
                        cachedPOSTRequest.id
                    } to queue due to timeout`,
                );
                return throwError(() => 'OFFLINE_STORE_SAVE_ERROR');
            }),
        );
    }

    private getCachedResponse(
        req: HttpRequest<unknown>,
        cacheableURL: string | RegExp,
    ): Observable<HttpResponse<unknown>> {
        return this.store.select(getCachedResponseByUrl, { url: cacheableURL }).pipe(
            map((state) => {
                if (typeof state !== 'undefined') {
                    return new HttpResponse({ body: state, status: 200 });
                }
                this.log.error(
                    `OfflineInterceptorService: getCachedResponse: there's no response cached yet for ${req.url}`,
                );
                throw new HttpErrorResponse({
                    status: HTTP_ERROR,
                    statusText: 'doing an offline call for non cached data',
                    url: req.url,
                });
            }),
            take(1),
        );
    }

    private getEndpoint(clonedReq: HttpRequest<unknown>): string | undefined {
        return clonedReq.url.toLowerCase().indexOf('deepinsert') > 0
            ? (<HttpRequest<CachedPayload>>clonedReq).body?.Endpoint
            : clonedReq.url;
    }
}
